// Roles and the permission matrix (01 §3.3). The Worker enforces; the UI only
// hides what the Worker would refuse anyway.

export const ROLES = ['viewer', 'dev', 'lead', 'admin'] as const
export type Role = (typeof ROLES)[number]
export type ProjectRole = Exclude<Role, 'admin'>

export const CAPABILITIES = {
  'project.read': 'viewer',
  'terminal.view': 'viewer',
  'terminal.write': 'dev',
  'session.chat': 'dev',
  'session.manage': 'dev',
  'job.delegate': 'dev',
  'job.retry': 'dev',
  'job.cancel': 'dev',
  // CI (features/21): run by hand, re-run, cancel.
  'ci.run': 'dev',
  'ticket.intake': 'dev',
  'project.settings': 'lead',
  'project.stage': 'lead',
  // Merge into the base branch and ship to prod (features/24): the click is the approval.
  'project.release': 'lead',
  'scope.tickets': 'lead',
  'feedback.manage': 'lead',
  'ticket.triage': 'lead',
  'project.secrets.write': 'lead',
  'project.repo.reconnect': 'lead',
  'project.people': 'lead',
  'container.restart': 'lead',
  'projects.create': 'admin',
  'projects.archive': 'admin',
  'people.invite': 'admin',
  'people.roles': 'admin',
  'people.reset2fa': 'admin',
  'servers.manage': 'admin',
  'integrations.manage': 'admin',
  'claude_accounts.manage': 'admin',
  // Opening a backup reads every project's secrets (features/23).
  'backups.restore': 'admin',
  // Scopes hold prices and hours (features/03).
  'scopes.manage': 'admin',
  // Admins approve every gate on our side (agency-workflow, decision 4).
  'scope.approve': 'admin',
} as const satisfies Record<string, Role>

export type Capability = keyof typeof CAPABILITIES

const RANK: Record<Role, number> = { viewer: 0, dev: 1, lead: 2, admin: 3 }

export function atLeast(role: Role, min: Role): boolean {
  return RANK[role] >= RANK[min]
}

/**
 * Effective role on one project: admin everywhere, else the project grant, else
 * none. No access is `null`, which the API answers with 404, not 403 — a
 * project you cannot see does not exist.
 */
export function effectiveRole(globalRole: Role, projectRole: ProjectRole | null | undefined): Role | null {
  if (globalRole === 'admin') return 'admin'
  return projectRole ?? null
}

export function can(role: Role | null, capability: Capability): boolean {
  return role !== null && atLeast(role, CAPABILITIES[capability])
}
